import React from 'react';

export type TimelineItem = {
  date?: string;
  title?: string;
  description?: string;
  details?: string;
};

export type TimelineEffects = {
  hover?: 'none' | 'lift' | 'glow' | 'scale';
  revealSpeed?: 'fast' | 'normal' | 'slow';
};

type Props = {
  items: TimelineItem[];
  editable?: boolean;
  onChange?: (items: TimelineItem[]) => void;
  className?: string;
  variant?: 'classic' | 'dots' | 'cards' | 'split';
  scrollable?: boolean;
  expandable?: boolean;
  effects?: TimelineEffects;
};

const Timeline: React.FC<Props> = ({ items, editable = false, onChange, className, variant = 'classic', scrollable = false, expandable = false, effects }) => {
  const add = () => {
    const next = [...items, { date: new Date().toISOString().slice(0,10), title: 'New milestone', description: 'Describe this step…' }];
    onChange && onChange(next);
  };
  const update = (i: number, patch: Partial<TimelineItem>) => {
    const next = items.map((it, idx) => idx === i ? { ...it, ...patch } : it);
    onChange && onChange(next);
  };
  const remove = (i: number) => {
    const next = items.filter((_, idx) => idx !== i);
    onChange && onChange(next);
  };

  const [openMap, setOpenMap] = React.useState<Record<number, boolean>>({});
  const toggle = (i: number) => setOpenMap(s => ({ ...s, [i]: !s[i] }));

  const hoverCls = React.useMemo(() => {
    switch (effects?.hover) {
      case 'lift':
        return 'transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md';
      case 'glow':
        return 'transition-shadow duration-200 hover:shadow-[0_0_0_3px_rgba(168,85,247,0.18)] hover:ring-1 hover:ring-purple-300';
      case 'scale':
        return 'transition-transform duration-200 hover:scale-[1.01]';
      default:
        return '';
    }
  }, [effects?.hover]);

  const revealDuration = effects?.revealSpeed === 'fast' ? 'duration-150' : effects?.revealSpeed === 'slow' ? 'duration-500' : 'duration-300';

  if (variant === 'split') {
    // Alternating left/right layout with a centered vertical line
    return (
      <div className={className || ''}>
        <div className="relative py-1">
          <div className="absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px bg-gradient-to-b from-purple-300 via-gray-200 to-blue-300" />
          <div className={`space-y-8 ${scrollable ? 'max-h-80 overflow-auto pr-2' : ''}`}>
            {items.map((it, i) => {
              const left = i % 2 === 0;
              return (
                <div key={i} className="relative grid grid-cols-1 md:grid-cols-2 items-start">
                  {left && <div className="hidden md:block" />}
                  <div className={`relative ${left ? 'md:col-start-1 md:text-right pr-8' : 'md:col-start-2 pl-8'}`}>
                    <span className={`absolute top-3 ${left ? 'right-[-7px]' : 'left-[-7px]'} inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white ring-2 ring-purple-500`}>
                      <span className="h-2 w-2 rounded-full bg-purple-600" />
                    </span>
                    <div className={`rounded-xl border border-purple-200 bg-gradient-to-br from-white to-purple-50 shadow-sm p-4 ${hoverCls}`}>
                      <div className="flex items-center justify-between gap-3">
                        <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700 ring-1 ring-purple-200">{it.date}</span>
                        <div className="font-semibold text-gray-900 truncate">{it.title}</div>
                        {expandable && (it.details || '').length > 0 && (
                          <button
                            type="button"
                            className="ml-2 inline-flex h-7 w-7 items-center justify-center rounded-md border border-purple-200 bg-white text-purple-700 hover:bg-purple-50"
                            onClick={()=> toggle(i)}
                            aria-expanded={!!openMap[i]}
                            title={openMap[i] ? 'Collapse' : 'Expand'}
                          >
                            <svg viewBox="0 0 24 24" className={`h-4 w-4 transition-transform ${openMap[i] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                          </button>
                        )}
                      </div>
                      {it.description && <div className="mt-2 text-sm text-gray-700">{it.description}</div>}
                      {expandable && (it.details || '').length > 0 && (
                        <div className={`overflow-hidden transition-all ${revealDuration} ${openMap[i] ? 'max-h-96 mt-3' : 'max-h-0'} `}>
                          <div className="rounded-lg border border-purple-100 bg-white/70 p-3 text-sm text-gray-700">
                            {it.details}
                          </div>
                        </div>
                      )}
                      {editable && (
                        <div className="mt-3 grid grid-cols-1 gap-2">
                          <input className="rounded-md border border-gray-200 px-2 py-1 text-xs" value={it.date || ''} onChange={(e)=> update(i, { date: e.target.value })} placeholder="YYYY-MM-DD" />
                          <input className="rounded-md border border-gray-200 px-2 py-1 text-sm" value={it.title || ''} onChange={(e)=> update(i, { title: e.target.value })} placeholder="Title" />
                          <textarea className="rounded-md border border-gray-200 px-2 py-1 text-sm" value={it.description || ''} onChange={(e)=> update(i, { description: e.target.value })} placeholder="Description" />
                          {expandable && (
                            <textarea className="rounded-md border border-gray-200 px-2 py-1 text-sm" value={it.details || ''} onChange={(e)=> update(i, { details: e.target.value })} placeholder="Expanded content (optional)" />
                          )}
                          <div className="flex items-center justify-end">
                            <button className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50" onClick={()=> remove(i)}>
                              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m-1 0l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                              Remove
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {!left && <div className="hidden md:block" />}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className || ''}>
      <div className="relative pl-6">
        {variant !== 'cards' && (
          <div className="absolute left-2 top-0 bottom-0 w-px bg-gradient-to-b from-purple-300 via-gray-200 to-blue-300" />
        )}
        <div className={`space-y-5 ${scrollable ? 'max-h-80 overflow-auto pr-2' : ''}`}>
          {items.map((it, i) => (
            <div key={i} className="relative">
              {variant !== 'cards' && (
                <span className="absolute -left-0.5 top-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white ring-2 ring-purple-500">
                  <span className={`h-2 w-2 rounded-full ${variant==='dots' ? 'bg-blue-600' : 'bg-purple-600'}`} />
                </span>
              )}
              <div className={`rounded-xl border p-3 shadow-sm ${variant==='cards' ? 'border-purple-200 bg-gradient-to-br from-white to-purple-50' : 'border-gray-200 bg-white'} ${hoverCls}`}>
                <div className="flex flex-wrap items-center gap-2">
                  {editable ? (
                    <input
                      className="rounded-md border border-gray-200 px-2 py-1 text-xs"
                      value={it.date || ''}
                      onChange={(e)=> update(i, { date: e.target.value })}
                      placeholder="YYYY-MM-DD"
                    />
                  ) : (
                    <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700 ring-1 ring-purple-200">{it.date}</span>
                  )}
                  {editable ? (
                    <input
                      className="flex-1 min-w-0 rounded-md border border-gray-200 px-2 py-1 text-sm"
                      value={it.title || ''}
                      onChange={(e)=> update(i, { title: e.target.value })}
                      placeholder="Title"
                    />
                  ) : (
                    <div className="font-semibold text-gray-900">{it.title}</div>
                  )}
                </div>
                <div className="mt-2">
                  {editable ? (
                    <textarea
                      className="w-full rounded-md border border-gray-200 px-2 py-1 text-sm"
                      value={it.description || ''}
                      onChange={(e)=> update(i, { description: e.target.value })}
                      placeholder="Description"
                    />
                  ) : (
                    <div className="text-sm text-gray-700">{it.description}</div>
                  )}
                </div>
                {expandable && (it.details || '').length > 0 && (
                  <div className={`overflow-hidden transition-all ${revealDuration} ${openMap[i] ? 'max-h-96 mt-3' : 'max-h-0'}`}>
                    <div className="rounded-lg border border-purple-100 bg-white/70 p-3 text-sm text-gray-700">
                      {it.details}
                    </div>
                  </div>
                )}
                {expandable && (it.details || '').length > 0 && (
                  <div className="mt-2 flex justify-end">
                    <button type="button" onClick={()=> toggle(i)} className="inline-flex items-center gap-1 rounded-md border border-purple-200 bg-white px-2 py-1 text-xs text-purple-700 hover:bg-purple-50">
                      <svg viewBox="0 0 24 24" className={`h-3.5 w-3.5 transition-transform ${openMap[i] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                      {openMap[i] ? 'Collapse' : 'Expand'}
                    </button>
                  </div>
                )}
                {editable && (
                  <div className="mt-2 flex items-center justify-end">
                    <button className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50" onClick={()=> remove(i)}>
                      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m-1 0l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
                      Remove
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {editable && (
        <div className="mt-3">
          <button className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-purple-700 hover:bg-purple-50" onClick={add}>
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
            Add item
          </button>
        </div>
      )}
    </div>
  );
};

export default Timeline;
